/**
 * PasskeyHandoffPage — phone-side single-purpose page that consumes a
 * recovery_session token to register a passkey on this device. Reached by
 * scanning the QR shown during /signup on the PC.
 *
 * No auth — the URL token IS the authority. Backend rate-limits issuance
 * (3/min/user via /handoff/init), and a single-use peek+revoke pattern
 * ensures one full register cycle per token.
 */

import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { registerPasskey } from "@/lib/passkey";

const PANEL_CLASS =
  "rounded-[32px] border border-[rgba(10,52,96,0.10)] bg-white/92 p-8 shadow-[0_24px_64px_rgba(10,52,96,0.10)] backdrop-blur-xl sm:p-10";
const PRIMARY_BUTTON_CLASS =
  "w-full rounded-full bg-[var(--cs-title-navy)] px-5 py-3.5 text-[15px] font-semibold text-white shadow-[0_16px_34px_rgba(10,52,96,0.18)] transition-colors duration-200 hover:bg-[var(--cs-title-navy)] disabled:cursor-not-allowed disabled:opacity-50";

type Phase = "ready" | "registering" | "done";

export function PasskeyHandoffPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [phase, setPhase] = useState<Phase>("ready");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("Ongeldige link.");
  }, [token]);

  const handleRegister = useCallback(async () => {
    setError("");
    setPhase("registering");
    try {
      await registerPasskey("Telefoon", { recoverySession: token });
      setPhase("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registratie mislukt.");
      setPhase("ready");
    }
  }, [token]);

  return (
    <div className="cs-marketing min-h-screen bg-[linear-gradient(180deg,#fcfbf7_0%,#ffffff_22%,#f3f9fd_100%)] px-4 py-12 sm:px-6 sm:py-16">
      <div className="mx-auto w-full max-w-md space-y-6">
        <div className={PANEL_CLASS}>
          <div className="mb-6 text-center">
            <h1 className="text-[32px] font-semibold tracking-[-0.04em] text-[var(--cs-title-navy)]">
              Passkey registreren
            </h1>
          </div>

          {error ? (
            <div className="mb-5 rounded-[18px] border border-[#F3C7CD] bg-[#FFF4F6] p-4 text-sm text-[#B42318]">
              {error}
            </div>
          ) : null}

          {phase === "done" ? (
            <p className="text-center text-[15px] text-[rgba(95,131,160,0.96)]">
              Klaar. U mag dit venster sluiten en teruggaan naar uw computer.
            </p>
          ) : token && !error ? (
            <>
              <p className="mb-6 text-center text-[15px] text-[rgba(95,131,160,0.96)]">
                Uw account en medische gegevens worden beschermd met een persoonlijke passkey.
                Registreer de passkey op dit toestel met uw vingerafdruk, gezichts-ID of pincode.
              </p>
              <button
                type="button"
                onClick={() => void handleRegister()}
                disabled={phase === "registering"}
                className={PRIMARY_BUTTON_CLASS}
              >
                {phase === "registering" ? "Bezig…" : "Passkey registreren"}
              </button>
            </>
          ) : (
            <p className="text-center text-[15px] text-[rgba(95,131,160,0.96)]">
              Open de QR-code opnieuw vanaf uw PC om een geldige link te krijgen.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
